"use client";
import type { ComponentPropsWithoutRef, HTMLAttributes } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "../input";

// ----------------------------------------------------------------------

interface CustomRHFInputProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<ComponentPropsWithoutRef<typeof Input>, "name" | "defaultValue"> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: Pick<HTMLAttributes<HTMLDivElement>, "className">;
  loading?: boolean;
}

export default function CustomRHFInput<TFieldValues extends FieldValues>({
  name,
  helperText,
  defaultValue,
  rules,
  containerClass,
  className,
  loading,
  onChange,
  onBlur,
  ...other
}: CustomRHFInputProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div className={cn("space-y-2 w-full", containerClass)}>
          <Input
            {...field}
            value={field.value ?? ""}
            {...other}
            browserOwned
            // Compose RHF's field handlers with any caller-supplied ones.
            // Spreading a bare `onChange`/`onBlur` here would REPLACE
            // field.onChange and silently break the controlled input.
            onChange={(event) => {
              field.onChange(event);
              onChange?.(event);
            }}
            onBlur={(event) => {
              field.onBlur();
              onBlur?.(event);
            }}
            disabled={loading}
            className={cn(className, {
              "border-semantic-error": !!error,
            })}
            size={4}
          />
          {!!helperText && (
            <p className="text-[10px] leading-tight text-text-disabled ml-1.5">
              {helperText}
            </p>
          )}
          {!!error && (
            <p className="text-xs leading-tight text-semantic-error ml-1.5">
              {error?.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
